import { z } from 'zod';
import { RecordIdSchema } from './identifiers';
import { InstantSchema } from './time';

/**
 * Cheque printing (COL-CHECK-004 extension): where each value goes on a bank's cheque leaf, set by an
 * administrator on the dashboard — no bank dimensions are built in.
 *
 * A layout is generic (any account), for one bank, or for one treasury account; the most specific
 * active one is offered first. A layout starts **unverified**: it may be test-printed on plain paper
 * or as an overlay on a drawn sample, and becomes verified only when someone records, with a reason,
 * that a test print matched a real leaf. Printing an unverified layout needs an explicit
 * acknowledgement and says so in the response.
 *
 * Printing never changes a cheque: it is not signed, approved, issued, delivered or paid by being
 * printed. Each print is recorded in the audit trail.
 */

export const CHEQUE_FIELDS = ['date', 'payee', 'amountNumeric', 'amountWords'] as const;
export type ChequeField = (typeof CHEQUE_FIELDS)[number];
export const CHEQUE_DATE_FORMATS = [
  'dd/MM/yyyy',
  'dd-MM-yyyy',
  'yyyy/MM/dd',
  'yyyy-MM-dd',
] as const;
/** Physical alignment on the leaf, the same in Arabic and English. */
export const CHEQUE_ALIGNMENTS = ['left', 'center', 'right'] as const;

const Millimetres = z.number().min(0).max(400);

export const ChequeFieldPositionSchema = z.strictObject({
  /** From the leaf's top-left corner, in millimetres. */
  xMm: Millimetres,
  yMm: Millimetres,
  widthMm: z.number().min(5).max(400),
  fontSizePt: z.number().min(6).max(24),
  align: z.enum(CHEQUE_ALIGNMENTS),
});
export type ChequeFieldPosition = z.infer<typeof ChequeFieldPositionSchema>;

const layoutFields = {
  name: z.string().trim().min(2).max(120),
  /** Absent both: generic. A bank, or one treasury account, narrows it. */
  bankId: RecordIdSchema.optional(),
  accountId: RecordIdSchema.optional(),
  paper: z.strictObject({
    widthMm: z.number().min(50).max(400),
    heightMm: z.number().min(30).max(300),
    orientation: z.enum(['landscape', 'portrait']),
  }),
  fields: z.strictObject({
    date: ChequeFieldPositionSchema,
    payee: ChequeFieldPositionSchema,
    amountNumeric: ChequeFieldPositionSchema,
    amountWords: ChequeFieldPositionSchema,
  }),
  dateFormat: z.enum(CHEQUE_DATE_FORMATS),
  /** The numeric amount: thousands separators, and the marks a bank expects around it (e.g. `#`). */
  amountFormat: z.strictObject({
    thousands: z.boolean(),
    prefix: z.string().max(4).default(''),
    suffix: z.string().max(4).default(''),
  }),
  wordsLanguage: z.enum(['ar', 'en']),
  /** Shift every field to correct the printer's feed, in millimetres (negative: left / up). */
  calibration: z.strictObject({
    offsetXMm: z.number().min(-30).max(30),
    offsetYMm: z.number().min(-30).max(30),
  }),
};

export const ChequeLayoutSchema = z.strictObject({
  layoutId: RecordIdSchema,
  legalEntityId: RecordIdSchema,
  ...layoutFields,
  /** The system's generic starting layout, clearly labelled; it must be calibrated before use. */
  starter: z.boolean(),
  verified: z.boolean(),
  verification: z
    .strictObject({ byAccountId: z.string(), at: InstantSchema, reason: z.string() })
    .optional(),
  state: z.enum(['active', 'retired']),
  version: z.number().int().positive(),
  createdAt: InstantSchema,
  updatedAt: InstantSchema,
});
export type ChequeLayout = z.infer<typeof ChequeLayoutSchema>;

export const CreateChequeLayoutSchema = z
  .strictObject({ legalEntityId: RecordIdSchema, ...layoutFields })
  .refine((value) => !(value.bankId && value.accountId), {
    message: 'LAYOUT_SCOPE_ONE_ONLY',
    path: ['accountId'],
  });
export type CreateChequeLayout = z.infer<typeof CreateChequeLayoutSchema>;

/** A change to a layout's geometry or format makes it unverified again. */
export const UpdateChequeLayoutSchema = z.strictObject({
  ...layoutFields,
  expectedVersion: z.number().int().positive(),
});
export type UpdateChequeLayout = z.infer<typeof UpdateChequeLayoutSchema>;

export const ChequeLayoutActionSchema = z.strictObject({
  expectedVersion: z.number().int().positive(),
  reason: z.string().trim().min(3).max(500),
});

export const ChequeTestPrintQuerySchema = z.strictObject({
  /** `plain`: values and guide boxes on plain paper; `overlay`: the same over a drawn sample leaf. */
  mode: z.enum(['plain', 'overlay']).default('plain'),
});

export const ChequePrintQuerySchema = z.strictObject({
  layoutId: RecordIdSchema,
  acknowledgeUnverified: z.enum(['true', 'false']).default('false'),
});

/**
 * The generic starter layout: a common landscape leaf. **Not** any bank's real dimensions — it is a
 * starting point to adjust and calibrate against the client's own leaves.
 */
export const STARTER_CHEQUE_LAYOUT = {
  paper: { widthMm: 175, heightMm: 80, orientation: 'landscape' as const },
  fields: {
    date: { xMm: 125, yMm: 10, widthMm: 40, fontSizePt: 11, align: 'center' as const },
    payee: { xMm: 25, yMm: 26, widthMm: 120, fontSizePt: 11, align: 'right' as const },
    amountWords: { xMm: 25, yMm: 38, widthMm: 115, fontSizePt: 10, align: 'right' as const },
    amountNumeric: { xMm: 130, yMm: 50, widthMm: 38, fontSizePt: 12, align: 'center' as const },
  },
  dateFormat: 'dd/MM/yyyy' as const,
  amountFormat: { thousands: true, prefix: '#', suffix: '#' },
  calibration: { offsetXMm: 0, offsetYMm: 0 },
};

export const CHEQUE_PRINT_AUDIT_ACTIONS = {
  layoutCreated: 'treasury.chequeLayout.created',
  layoutUpdated: 'treasury.chequeLayout.updated',
  layoutVerified: 'treasury.chequeLayout.verified',
  layoutRetired: 'treasury.chequeLayout.retired',
  printed: 'treasury.cheque.printed',
} as const;
